<template>
  <div class="landing">
    <BlogNav />

    <!-- HERO -->
    <section class="hero">
      <div class="hero-inner">
        <h1 class="hero-title">
          Couverture jurisprudentielle :<br />
          <span class="font-bold">Exhaustive & Augmentée</span>
        </h1>
        <p class="hero-sub">
          Une base fondée sur l'open data et augmentée par l'IA
        </p>
      </div>
    </section>

    <!-- INTRO -->
    <section class="content-section">
      <div class="content-inner">
        <p class="intro-text">
          À l’heure où la recherche juridique est encore trop souvent associée à des bases privées historiques, une réalité factuelle s’impose progressivement : la jurisprudence française est aujourd’hui massivement accessible en open data, et certains outils ont fait le choix stratégique de bâtir leur puissance non sur l’accumulation éditoriale, mais sur l’exhaustivité réelle des données publiques et leur exploitation intelligente.
        </p>
        <p class="intro-text">
          Query Juriste s’inscrit pleinement dans cette nouvelle génération d’outils juridiques. Contrairement aux idées reçues, sa couverture jurisprudentielle est exhaustive, vérifiable et chiffrée, tant pour l’ordre judiciaire que pour l’ordre administratif, tout en intégrant la jurisprudence européenne et de nombreuses sources juridiques complémentaires spécialisées.
        </p>
        <p class="intro-text">
          Cet article a pour objectif de poser un état des lieux clair, factuel et documenté.
        </p>
      </div>
    </section>

    <!-- 1. COUVERTURE COMPLETE -->
    <section class="feature-section">
      <div class="feature-inner">
        <h2 class="section-title">1. Une couverture complète de la jurisprudence française</h2>

        <div class="feature-card">
          <div class="feature-number">1</div>
          <div class="feature-content">
            <h3 class="feature-heading">Plus de 2,7 millions de décisions accessibles</h3>
            <p class="feature-text">
              Query Juriste repose sur l’intégralité de la jurisprudence française disponible en open data, soit plus de 2,7 millions de décisions à ce jour.
            </p>
            <p class="feature-text">
              Cette couverture inclut : Les juridictions de l’ordre judiciaire, Les juridictions de l’ordre administratif, Les décisions rendues par les juridictions suprêmes.
            </p>
            <p class="feature-highlight">
              Loin d’être partielle, cette base reflète la réalité actuelle de la production juridictionnelle française telle qu’elle est publiée par les institutions publiques.
            </p>
          </div>
        </div>

        <div class="feature-card">
          <div class="feature-number">2</div>
          <div class="feature-content">
            <h3 class="feature-heading">Ordre judiciaire</h3>
            <p class="feature-text">
              La base couvre notamment :
            </p>
            <ul class="feature-list">
              <li>Tribunal judiciaire</li>
              <li>Cour d’appel</li>
              <li>Cour de cassation</li>
            </ul>
            <p class="feature-highlight">
              Ces décisions permettent d’analyser aussi bien la pratique contentieuse de première instance que la construction de la jurisprudence au plus haut niveau.
            </p>
          </div>
        </div>

        <div class="feature-card">
          <div class="feature-number">3</div>
          <div class="feature-content">
            <h3 class="feature-heading">Ordre administratif</h3>
            <p class="feature-text">
              Sont également intégrées :
            </p>
            <ul class="feature-list">
              <li>Tribunaux administratifs</li>
              <li>Cours administratives d’appel</li>
              <li>Conseil d’État</li>
            </ul>
            <p class="feature-highlight">
              Cette couverture est essentielle pour les praticiens du droit public, du droit de l’urbanisme, du droit fiscal ou encore du droit de la fonction publique.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- 2. JURISPRUDENCE EUROPEENNE -->
    <section class="content-section bg-surface">
      <div class="content-inner">
        <h2 class="section-title">2. Jurisprudence européenne : intégration de la CJUE</h2>
        <p class="intro-text">
          Query Juriste ne se limite pas au périmètre national. L’outil intègre également la jurisprudence de la Cour de justice de l’Union européenne (CJUE).
        </p>
        <p class="intro-text">
          Cette dimension européenne est indispensable pour :
        </p>
        <ul class="simple-list">
          <li>Le droit de la concurrence</li>
          <li>Le droit de la consommation</li>
          <li>Le droit social</li>
          <li>Le droit du numérique</li>
          <li>Le droit de l’environnement</li>
        </ul>
        <p class="intro-text mt-4">
          Elle permet aux juristes et avocats d’articuler efficacement droit interne et droit de l’Union.
        </p>
      </div>
    </section>

    <!-- 3. OPEN DATA JUDICIAIRE -->
    <section class="feature-section">
      <div class="feature-inner">
        <h2 class="section-title">3. Open data judiciaire : une exhaustivité méconnue</h2>
        
        <div class="feature-card">
          <div class="feature-content ml-0">
            <p class="feature-text">
              Pendant longtemps, l’open data judiciaire a été perçue comme incomplète ou insuffisante. Cette perception est aujourd’hui dépassée.
            </p>
            <p class="feature-text">
              La politique publique de diffusion des décisions de justice a profondément transformé l’accès au droit. L’essentiel de la jurisprudence est désormais accessible, structurée et exploitable.
            </p>
            <p class="feature-text">
              Query Juriste s’appuie sur cette réalité institutionnelle, sans dépendre d’archives privées héritées.
            </p>
            <p class="feature-highlight">
              L’exhaustivité ne se confond pas avec l’édition. Là où certaines bases reposent sur des décennies d’enrichissement éditorial, Query Juriste fait le choix d’une couverture complète des décisions disponibles, combinée à une capacité d’analyse juridique avancée.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- 4. SOURCES COMPLEMENTAIRES -->
    <section class="content-section">
      <div class="content-inner">
        <h2 class="section-title">4. Des sources juridiques complémentaires spécialisées</h2>
        <p class="intro-text">
          Au-delà de la jurisprudence stricto sensu, Query Juriste intègre de très nombreuses sources juridiques complémentaires, notamment dans des domaines spécifiques du droit :
        </p>
        <div class="grid-sources">
          <div class="source-item">Droit social</div>
          <div class="source-item">Droit fiscal</div>
          <div class="source-item">Droit de la propriété intellectuelle</div>
          <div class="source-item">Corporate M&A</div>
          <div class="source-item">Droit public</div>
        </div>
        <p class="intro-text mt-4">
          Ces sources permettent de contextualiser les décisions, d’enrichir le raisonnement juridique et d’offrir une vision transversale du droit applicable.
        </p>
      </div>
    </section>

    <!-- 5. APPORT IA -->
    <section class="feature-section bg-dark text-white">
      <div class="feature-inner">
        <h2 class="section-title text-white">5. L’apport décisif de l’IA juridique</h2>
        
        <p class="feature-text text-white-80">
          Disposer de millions de décisions n’est qu’un point de départ. La véritable valeur ajoutée réside dans la capacité à :
        </p>
        <ul class="simple-list text-white">
          <li>Identifier la jurisprudence pertinente</li>
          <li>Comprendre les raisonnements des juridictions</li>
          <li>Mettre en perspective les décisions</li>
        </ul>
        
        <p class="feature-highlight border-white text-white mt-6">
          Query Juriste ne se contente pas d’indexer : il raisonne juridiquement.
        </p>

        <p class="feature-text text-white-80 mt-6">
          L’outil est conçu pour répondre aux usages réels des avocats, juristes et directions juridiques : Analyse stratégique, Préparation d’argumentation, Sécurisation juridique.
        </p>
        <p class="feature-text text-white-80">
          Cette approche distingue fondamentalement Query Juriste des bases purement documentaires.
        </p>
      </div>
    </section>

    <!-- 6. VS BASES TRADITIONNELLES -->
    <section class="content-section">
      <div class="content-inner">
        <h2 class="section-title">6. Query Juriste face aux bases juridiques traditionnelles</h2>
        <p class="intro-text">
          Comparer Query Juriste à des bases historiques uniquement sous l’angle de l’exhaustivité est réducteur. La réalité est la suivante :
        </p>
        <ul class="summary-list">
          <li>
            <img class="check" src="@/assets/check.svg" alt="" />
            La couverture jurisprudentielle est exhaustive
          </li>
          <li>
            <img class="check" src="@/assets/check.svg" alt="" />
            Les sources sont officielles et vérifiables
          </li>
          <li>
            <img class="check" src="@/assets/check.svg" alt="" />
            L’analyse juridique est au cœur de l’outil
          </li>
        </ul>
        <p class="intro-text feature-highlight">
          Query Juriste ne remplace pas simplement une base : il propose un nouveau paradigme de recherche et de raisonnement juridique.
        </p>
      </div>
    </section>

    <!-- CONCLUSION -->
    <section class="summary-section bg-surface">
      <div class="summary-inner">
        <h2 class="section-title">Conclusion</h2>
        <p class="summary-main">
          Query Juriste repose sur une certitude factuelle : la jurisprudence française et européenne est aujourd’hui massivement accessible, et son exploitation intelligente constitue le véritable enjeu.
        </p>
        <p class="summary-text">
          Avec plus de 2,7 millions de décisions, une couverture complète des ordres juridictionnels, l’intégration de la CJUE et de nombreuses sources spécialisées, Query Juriste offre une base exhaustive, augmentée par une IA conçue pour raisonner comme un juriste.
        </p>
        <p class="summary-conclusion">
          <strong>La question n’est donc plus de savoir si la couverture est suffisante, mais comment transformer cette masse jurisprudentielle en intelligence juridique opérationnelle.</strong>
        </p>
      </div>
    </section>

    <!-- CTA BAND -->
    <section class="cta-band">
      <div class="cta-inner">
        <div class="cta-copy">
          <h3 class="cta-title">Une démo personnalisée, sans engagement</h3>
          <p class="cta-text">
            Découvrez comment notre outil révolutionne le travail des avocats et juristes.
          </p>
        </div>

        <div class="cta-form">
          <a
            class="btn btn-ghost rounded-pill"
            href="https://calendly.com/d/cytz-wbs-3xn/demo-query-juriste-ia-recherche-juridique"
            @click.prevent="openCalendlyDemo"
          >
            Réserver une démo
          </a>
          <p class="cta-foot">Tarifs sur mesure, adaptés à votre structure</p>
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="footer">
      <div class="footer-content">
        <div class="footer-logo-section">
          <img src="/logo-query-juriste-png.png" alt="Query Juriste" class="logo" />
          <p class="footer-description">
            L'IA juridique qui raisonne avec vos besoins.
            Simplifiez vos recherches juridiques.
          </p>
        </div>

        <div class="footer-links-grid">
          <div class="footer-column">
            <h4>Pages</h4>
            <ul>
              <li><a href="https://www.query-juriste.com/">Accueil</a></li>
              <li><a href="https://www.query-juriste.com/blog">Blog</a></li>
              <li><a href="https://www.query-juriste.com/ia">L'IA Query Juriste</a></li>
              <li><a href="https://www.query-juriste.com/sources">Sources & couverture</a></li>
              <li><a href="https://www.query-juriste.com/faq">FAQ</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>Produit</h4>
            <ul>
              <li><a href="https://www.query-juriste.com/loginPage">Connexion</a></li>
              <li><a href="https://www.query-juriste.com/contactPage">Contact</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>Entreprise</h4>
            <ul>
              <li><a href="https://www.query-juriste.com/mentionsPage">Mentions légales</a></li>
              <li><a href="https://www.query-juriste.com/conditions-utilisation">CGU</a></li>
              <li><a href="https://www.query-juriste.com/conditions-vente">CGV</a></li>
              <li><a href="https://www.query-juriste.com/politique-donnees">Confidentialité</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <p>© 2025 Query Juriste, tous droits réservés</p>
      </div>
    </footer>
  </div>
</template>

<script>
import BlogNav from "@/components/BlogNav.vue";
import { openCalendlyDemo } from "@/utils/calendly";

export default {
  name: "SourcesCouverturePage",
  components: { BlogNav },
  methods: {
    openCalendlyDemo,
  },
  metaInfo() {
    return {
      title: "Sources & couverture - Query Juriste",
      meta: [
        {
          name: "description",
          content:
            "Découvrez la couverture complète de la jurisprudence française et européenne par Query Juriste : 3M+ décisions en open data, analyse IA avancée.",
        },
      ],
      link: [{ rel: "canonical", href: "https://www.query-juriste.com/sources" }],
    };
  },
};
</script>

<style scoped>
/* ---------- Tokens ---------- */
:root {
  --bg: #ffffff;
  --text: #1a1a1a;
  --muted: #5e6470;
  --primary: #7e5cf2;
  --primary-600: #625CFB;
  --info: #625CFB;
  --surface: #f7f8fa;
  --border: #e6e8ec;
  --shadow: 0 10px 30px rgba(0,0,0,.12);
  --shadow-sm: 0 4px 16px rgba(0,0,0,.08);
  --radius: 14px;
}

/* ---------- Utilities ---------- */
.text-primary { color: #625CFB; }
.text-info { color: #625CFB; }
.font-bold { font-weight: 700; color: #625CFB; }
.rounded-xl { border-radius: 20px; }
.rounded-pill { border-radius: 999px; }
.shadow-lg { box-shadow: var(--shadow); }
.shadow-sm { box-shadow: var(--shadow-sm); }

.bg-surface { background: #f8f9fc; }
.bg-dark { background: #1a1a1a; }
.text-white { color: #fff; }
.text-white-80 { color: rgba(255,255,255,0.8); }
.border-white { border-color: #fff !important; }

.mt-4 { margin-top: 16px; }
.mt-6 { margin-top: 24px; }
.ml-0 { margin-left: 0 !important; }

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 22px;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid transparent;
  cursor: pointer;
  transition: transform .06s ease, background .2s ease, color .2s ease, border-color .2s ease;
  will-change: transform;
}
.btn:active { transform: translateY(1px); }

.btn-ghost {
  background: transparent;
  border-color: #fff;
  color: #fff;
}
.btn-ghost:hover {
  background: rgba(255,255,255,.08);
  border-color: #fff;
}

/* ---------- Layout wrappers ---------- */
.landing {
  font-family: 'Poppins', ui-sans-serif, system-ui, sans-serif;
  background: var(--bg);
}

/* ---------- HERO ---------- */
.hero {
  padding: 110px 20px 40px;
  background: linear-gradient(180deg, #f8f9fc 0%, #ffffff 100%);
}
.hero-inner {
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
}
.hero-title {
  font-size: clamp(32px, 5vw, 52px);
  line-height: 1.15;
  margin-bottom: 0.8em;
}
.hero-sub {
  margin: 10px 0 24px;
  color: #5e6470;
  font-size: clamp(16px, 2.6vw, 20px);
}

/* ---------- CONTENT SECTION ---------- */
.content-section {
  padding: 50px 20px;
}
.content-inner {
  max-width: 800px;
  margin: 0 auto;
}
.intro-text {
  font-size: 18px;
  line-height: 1.7;
  color: #1a1a1a;
  margin-bottom: 20px;
}
.intro-text strong {
  color: #625CFB;
}

/* ---------- FEATURE SECTION ---------- */
.feature-section {
  padding: 60px 20px;
  background: #f8f9fc;
}
.bg-dark.feature-section {
  background: #1a1a1a;
}
.feature-inner {
  max-width: 900px;
  margin: 0 auto;
}
.section-title {
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  margin-bottom: 40px;
  text-align: center;
}
.feature-card {
  display: flex;
  gap: 24px;
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0,0,0,.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.feature-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0,0,0,.1);
}
.feature-number {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #625CFB;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
}
.feature-content {
  flex: 1;
}
.feature-heading {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 16px;
  color: #1a1a1a;
}
.feature-text {
  font-size: 16px;
  line-height: 1.7;
  color: #5e6470;
  margin: 0 0 12px;
}
.text-white .feature-text {
  color: rgba(255,255,255,0.8);
}
.feature-highlight {
  font-size: 16px;
  line-height: 1.7;
  color: #1a1a1a;
  font-weight: 500;
  margin: 0;
  padding-left: 16px;
  border-left: 3px solid #625CFB;
}
.feature-list {
  padding-left: 20px;
  margin-bottom: 12px;
  color: #5e6470;
  font-size: 16px;
}
.simple-list {
  padding-left: 20px;
  font-size: 17px;
  line-height: 1.8;
  color: #1a1a1a;
}
.simple-list.text-white {
  color: #fff;
}

/* ---------- SUMMARY SECTION ---------- */
.summary-section {
  padding: 70px 20px;
}
.summary-inner {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}
.summary-main {
  font-size: 22px;
  line-height: 1.6;
  color: #1a1a1a;
  margin-bottom: 24px;
}
.summary-text {
  font-size: 18px;
  color: #5e6470;
  margin-bottom: 24px;
}
.summary-list {
  list-style: none;
  padding: 0;
  margin: 0 auto 32px;
  max-width: 500px;
  text-align: left;
}
.summary-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  font-size: 17px;
  font-weight: 500;
  color: #1a1a1a;
}
.summary-list .check {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}
.summary-conclusion {
  font-size: 18px;
  line-height: 1.7;
  color: #5e6470;
  max-width: 700px;
  margin: 0 auto;
}

/* ---------- Grid Sources ---------- */
.grid-sources {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
}
.source-item {
  background: #f0f0f5;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  color: #333;
}

/* ---------- CTA BAND ---------- */
.cta-band {
  background: #625CFB;
  color: #fff;
  margin: 0;
  padding: 50px 20px;
}
.cta-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  align-items: center;
}
.cta-title {
  font-weight: 500;
  font-size: clamp(22px, 3.6vw, 32px);
  margin: 0 0 8px;
}
.cta-text { margin: 0; opacity: .95; }
.cta-foot { margin-top: 10px; opacity: .95; }

/* ---------- FOOTER ---------- */
.footer {
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  background-color: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 3rem 0 0 0;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 3rem;
  display: flex;
  justify-content: space-between;
  gap: 4rem;
  align-items: start;
}

.footer-logo-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.footer-logo-section .logo {
  height: 60px;
  width: 60px;
  align-items: center;
  margin-bottom: 0.5rem;
}

.footer-description {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0;
  max-width: 280px;
}

.footer-links-grid {
  display: flex;
  gap: 3rem;
}

.footer-column h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.footer-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.footer-column li {
  margin: 0;
}

.footer-column a {
  color: #6b7280;
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.2s ease;
  line-height: 1.4;
}

.footer-column a:hover {
  color: #625CFB;
}

.footer-bottom {
  margin-top: 2rem;
  padding: 1.5rem 0;
  border-top: 1px solid #e5e7eb;
  background-color: #f8fafc;
}

.footer-bottom p {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 3rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

/* ---------- Responsive ---------- */
@media (max-width: 1100px) {
  .cta-inner {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}

@media (max-width: 768px) {
  .feature-card {
    flex-direction: column;
    padding: 24px;
  }
  .feature-number {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }
}

@media (max-width: 640px) {
  .hero { padding-top: 80px; }
  .grid-sources {
    flex-direction: column;
  }
}

/* Footer responsive */
@media (max-width: 1024px) {
  .footer-content {
    flex-direction: column;
    gap: 3rem;
  }
  
  .footer-links-grid {
    width: 100%;
    justify-content: space-between;
  }
}

@media (max-width: 640px) {
  .footer-links-grid {
    flex-direction: column;
    gap: 2rem;
  }
  
  .footer-content {
    padding: 0 1.5rem;
  }
}
</style>
